import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stepper, Step,
  StepLabel, Alert, CircularProgress, Chip, Grid, TextField,
  Select, MenuItem, FormControl, InputLabel, LinearProgress,
  List, ListItem, ListItemText, ListItemIcon, Divider
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RouterIcon       from '@mui/icons-material/Router';
import WifiIcon         from '@mui/icons-material/Wifi';
import ExploreIcon      from '@mui/icons-material/Explore';
import { sitesAPI, walkthroughAPI, sensorsAPI } from '../services/api';

const STEPS = ['Selectionner le site', 'Positionner les capteurs', 'Finaliser'];

export default function Walkthrough() {
  const [step,        setStep]        = useState(0);
  const [sites,       setSites]       = useState([]);
  const [siteId,      setSiteId]      = useState('');
  const [technician,  setTechnician]  = useState('Technicien REDIA');
  const [session,     setSession]     = useState(null);
  const [sensors,     setSensors]     = useState([]);
  const [currentSensor, setCurrentSensor] = useState(null);
  const [rssiReadings,  setRssiReadings]  = useState({});
  const [checkpointResult, setCheckpointResult] = useState(null);
  const [confirmed,   setConfirmed]   = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');

  useEffect(() => {
    sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const startSession = async () => {
    setLoading(true); setError('');
    try {
      const r = await walkthroughAPI.start({ site_id: siteId, technician });
      setSession(r.data);
      setSensors(r.data.all_sensors || []);
      const toPosition = r.data.sensors_to_position || [];
      if (toPosition.length > 0) {
        setCurrentSensor(toPosition[0]);
      }
      setStep(1);
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur demarrage session');
    } finally { setLoading(false); }
  };

  const sendCheckpoint = async () => {
    if (!currentSensor) return;
    setLoading(true); setError('');
    try {
      const readings = {};
      Object.entries(rssiReadings).forEach(([k, v]) => {
        if (v !== '') readings[k] = parseInt(v);
      });
      const r = await walkthroughAPI.checkpoint({
        session_id: session.session_id,
        checkpoint_type: 'sensor',
        label: currentSensor,
        rssi_readings: readings,
        confirm: true
      });
      setCheckpointResult(r.data);
      setConfirmed(prev => [...prev, currentSensor]);
      setSuccess(`Position confirmee : X=${r.data.position.x_cm}cm Y=${r.data.position.y_cm}cm — Precision ~${r.data.position.precision_m}m`);

      // Passer au capteur suivant
      const toPosition = session.sensors_to_position || [];
      const remaining  = toPosition.filter(s => ![...confirmed, currentSensor].includes(s));
      if (remaining.length > 0) {
        setCurrentSensor(remaining[0]);
        setRssiReadings({});
        setCheckpointResult(null);
        setSuccess('');
      } else {
        setCurrentSensor(null);
      }
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur calcul position');
    } finally { setLoading(false); }
  };

  const finalizeSession = async () => {
    setLoading(true);
    try {
      await walkthroughAPI.finalize(session.session_id);
      setStep(2);
      setSuccess('Session finalisee ! Positions sauvegardees.');
    } catch(e) {
      setError('Erreur finalisation');
    } finally { setLoading(false); }
  };

  const allSensorsPositioned = session &&
    (session.sensors_to_position?.length === 0 || confirmed.length >= session.sensors_to_position?.length);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1F4E79" mb={1}>
        Walkthrough — Positionnement capteurs
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Positionnez les capteurs automatiquement par deplacement physique
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* STEP 0 — Selectionner site */}
      {step === 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Configurer la session
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Boutique</InputLabel>
                  <Select value={siteId} label="Boutique"
                    onChange={e => setSiteId(e.target.value)}>
                    {sites.map(s => (
                      <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Nom technicien"
                  value={technician} onChange={e => setTechnician(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" size="large" onClick={startSession}
                  disabled={!siteId || loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ExploreIcon />}
                  sx={{ bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}>
                  Demarrer le walkthrough
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* STEP 1 — Positionner capteurs */}
      {step === 1 && session && (
        <Grid container spacing={3}>
          {/* Capteur maitre */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '2px solid #1F4E7930' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <RouterIcon color="primary" />
                  <Typography fontWeight={600}>Capteur maitre (Origine)</Typography>
                </Box>
                <Chip label={session.master_sensor?.sensor_id} sx={{ mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Position fixe : X=0cm Y=0cm
                </Typography>
                <Alert severity="info" sx={{ mt: 2, fontSize: '0.8rem' }}>
                  {session.master_sensor?.message}
                </Alert>
              </CardContent>
            </Card>

            {/* Progression */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography fontWeight={600} mb={2}>Progression</Typography>
                <List dense>
                  {sensors.filter(s => !s.is_master).map(s => (
                    <ListItem key={s.sensor_id} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {confirmed.includes(s.sensor_id)
                          ? <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                          : <RadioButtonUncheckedIcon sx={{ color: '#ccc', fontSize: 20 }} />}
                      </ListItemIcon>
                      <ListItemText
                        primary={s.sensor_id}
                        secondary={confirmed.includes(s.sensor_id)
                          ? 'Positionne' : 'En attente'}
                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {allSensorsPositioned && (
                  <Button fullWidth variant="contained" onClick={finalizeSession}
                    disabled={loading} sx={{ mt: 2, bgcolor: '#4CAF50',
                      '&:hover': { bgcolor: '#388E3C' } }}>
                    Finaliser la session
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Checkpoint actuel */}
          <Grid item xs={12} md={8}>
            {currentSensor ? (
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Positionner : {currentSensor}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3, fontSize: '0.85rem' }}>
                    Approchez-vous a moins de 30cm du capteur {currentSensor}, puis saisissez les signaux RSSI detectes.
                  </Alert>

                  <Typography fontWeight={600} mb={2}>
                    Signaux RSSI des capteurs (dBm)
                  </Typography>
                  <Grid container spacing={2}>
                    {sensors.map(s => (
                      <Grid item xs={12} sm={6} key={s.sensor_id}>
                        <TextField
                          fullWidth size="small"
                          label={`${s.sensor_id} ${s.is_master ? '(Maitre)' : ''}`}
                          placeholder="-65"
                          value={rssiReadings[s.sensor_id] || ''}
                          onChange={e => setRssiReadings(prev =>
                            ({...prev, [s.sensor_id]: e.target.value}))}
                          type="number"
                          InputProps={{ startAdornment:
                            <WifiIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} /> }}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Button variant="contained" size="large" onClick={sendCheckpoint}
                    disabled={loading || Object.keys(rssiReadings).length === 0}
                    sx={{ mt: 3, bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}
                    startIcon={loading ? <CircularProgress size={18} color="inherit"/> : <CheckCircleIcon />}>
                    Calculer et confirmer la position
                  </Button>

                  {checkpointResult && (
                    <Card sx={{ mt: 2, bgcolor: '#E8F5E9', borderRadius: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Typography fontWeight={600} color="#2E7D32">
                          Position calculee
                        </Typography>
                        <Grid container spacing={1} mt={0.5}>
                          {[
                            ['X', `${checkpointResult.position?.x_cm} cm`],
                            ['Y', `${checkpointResult.position?.y_cm} cm`],
                            ['Precision', `~${checkpointResult.position?.precision_m} m`],
                            ['Capteur proche', checkpointResult.closest_sensor],
                          ].map(([k,v]) => (
                            <Grid item xs={6} key={k}>
                              <Typography variant="caption" color="text.secondary">{k}</Typography>
                              <Typography fontWeight={600} fontSize="0.9rem">{v}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                textAlign: 'center', p: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color="#4CAF50">
                  Tous les capteurs sont positionnes !
                </Typography>
                <Typography color="text.secondary" mt={1}>
                  Cliquez sur "Finaliser la session" pour sauvegarder.
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      {/* STEP 2 — Terminé */}
      {step === 2 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          textAlign: 'center', p: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} color="#4CAF50">
            Walkthrough termine !
          </Typography>
          <Typography color="text.secondary" mt={1} mb={3}>
            Toutes les positions ont ete sauvegardees en base de donnees.
            La heatmap est maintenant operationnelle.
          </Typography>
          <Button variant="outlined" onClick={() => {
            setStep(0); setSession(null); setConfirmed([]);
            setCurrentSensor(null); setRssiReadings({});
          }}>
            Nouveau walkthrough
          </Button>
        </Card>
      )}
    </Box>
  );
}
